import useToastContext from "hooks/useToastContext";
import { apiPut, apiDelete } from "../../utils/apiFetch";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import apiPath from "../../utils/apiPath";
import { useContext, useEffect, useState } from "react";
import { isEmpty } from "lodash";
import AuthContext from "context/AuthContext";
import { BsArrowUpShort } from "react-icons/bs";
import { AiFillEye, AiFillEdit, AiFillDelete } from "react-icons/ai";
import { Switch } from '@headlessui/react';
import ConfirmationModal from '../../components/reusable/ConfirmationModal';

const VideoList = ({
  videoList,
  page,
  getVideos,
  handleEdit,
  sort,
  setSort,
  handleView,
  manager,
  isLoading
}) => {
  const notification = useToastContext();
  const { t } = useTranslation();
  const { user, updatePageName } = useContext(AuthContext);
  const [loadingStates, setLoadingStates] = useState({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    item: null
  });

  const handleStatusChange = async (item) => {
    try {
      setLoadingStates(prev => ({ ...prev, [item._id]: true }));

      const payload = {
        status: item?.status === "inactive" ? "active" : "inactive",
      };
      const path = `${apiPath.changeVideoStatus}/${item?._id}`;
      const result = await apiPut(path, payload);

      if (result?.status === 200) {
        notification.success(result.data.message);
        getVideos();
      } else if (result?.status === 404) {
        notification.error(t("VIDEO_NOT_FOUND"));
      }
    } catch (error) {
      console.error("Error in status change:", error.message);
      notification.error(error.message || t("STATUS_UPDATE_FAILED"));
    } finally {
      setLoadingStates(prev => ({ ...prev, [item._id]: false }));
    }
  };
  const handleFeaturedToggle = async (item) => {
    try {
      setLoadingStates(prev => ({ ...prev, [item._id]: true }));

      // If trying to feature a video and another one is already featured
      if (!item.isFeatured) {
        const featuredVideo = videoList.find(video => video.isFeatured);
        if (featuredVideo) {
          notification.error(t("ONLY_ONE_FEATURED_VIDEO_ALLOWED"));
          return;
        }
      }

      const path = `${apiPath.toggleFeaturedVideo}/${item?._id}`;
      const result = await apiPut(path, { isFeatured: !item.isFeatured });

      if (result?.status === 200) {
        notification.success(result.data.message);
        getVideos();
      } else {
        notification.error(result?.data?.message || t("UPDATE_FAILED"));
      }
    } catch (error) {
      console.error("Error in featured toggle:", error.message);
      notification.error(error.message || t("UPDATE_FAILED"));
    } finally {
      setLoadingStates(prev => ({ ...prev, [item._id]: false }));
    }
  };

  const handleDelete = async (item) => {
    try {
      setLoadingStates(prev => ({ ...prev, delete: true }));
      const path = `${apiPath.deleteVideo}/${item._id}`;

      const result = await apiDelete(path);

      if (result?.status === 200) {
        notification.success(t("VIDEO_DELETED_SUCCESS"));
        getVideos();
      } else {
        notification.error(result?.data?.message || t("DELETE_FAILED"));
      }
    } catch (error) {
      console.error("Delete error:", error);
      notification.error(error.message || t("DELETE_FAILED"));
    } finally {
      setLoadingStates(prev => ({ ...prev, delete: false }));
      setDeleteModal({ isOpen: false, item: null });
    }
  };

  const getVideoTypeBadge = (type) => {
    const colors = {
      youtube: 'bg-red-100 text-red-800',
      upload: 'bg-blue-100 text-blue-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${colors[type]}`}>
        {type === 'youtube' ? 'YouTube' : 'Uploaded'}
      </span>
    );
  };

  return (
    <div>
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        onConfirm={() => handleDelete(deleteModal.item)}
        title={t("CONFIRM_DELETE")}
        message={t("ARE_YOU_SURE_DELETE_THIS_VIDEO", { title: deleteModal.item?.title })}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={loadingStates.delete}
        isDanger={true}
      />

      <div className="p-3">
        <div className="overflow-x-auto relative rounded-lg border">
          <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
            <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className="py-3 px-6">
                  {t("SR_NO")}
                </th>
                <th scope="col" className="py-3 px-6">
                  {t("TITLE")}
                </th>
                <th scope="col" className="py-3 px-6">
                  {t("VIDEO_TYPE")}
                </th>
                <th
                  scope="col"
                  onClick={() => {
                    const newSortType = sort.sortType === "asc" ? "desc" : "asc";
                    setSort({
                      sortType: newSortType,
                      sortBy: "createdAt",
                    });
                  }}
                  className="cursor-pointer px-6 py-3"
                >
                  <div className="flex justify-start">
                    <span>{t("CREATED_DATE")}</span>
                    <span>
                      {sort.sortBy === "createdAt" && (
                        <BsArrowUpShort
                          className={`w-4 h-4 ${sort.sortType === "asc" ? "" : "rotate-180"
                            }`}
                        />
                      )}
                    </span>
                  </div>
                </th>
                <th scope="col" className="py-3 px-6">
                  {t("STATUS")}
                </th>
                <th scope="col" className="py-3 px-6">
                  {t("FEATURED")}
                </th>
                <th scope="col" className="py-3 px-6 text-center">
                  {t("ACTIONS")}
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="6" className="py-4 px-6 text-center">
                    {t("LOADING")}...
                  </td>
                </tr>
              ) : videoList?.length > 0 ? (
                videoList.map((item, i) => (
                  <tr
                    key={item._id}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <td className="py-4 px-6 border-r font-medium text-gray-900">
                      {i + 1 + 10 * (page - 1)}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900">
                      {item?.title}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900">
                      {getVideoTypeBadge(item?.videoType)}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900">
                      {dayjs(item?.createdAt).format("MM-DD-YYYY hh:mm A")}
                    </td>
                    <td className="py-4 px-6 border-r">
                      <div className="flex items-center">
                        <Switch
                          checked={item.status === "active"}
                          onChange={() => handleStatusChange(item)}
                          disabled={loadingStates[item._id] || !(user?.role === "admin" || manager?.status)}
                          className={`${item.status === "active" ? "bg-LightBlue" : "bg-gray-200"
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-LightBlue ${loadingStates[item._id] ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        >
                          <span
                            className={`${item.status === "active" ? "translate-x-6" : "translate-x-1"
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>
                    </td>
                    <td className="py-4 px-6 border-r">
                      <div className="flex items-center">
                        <Switch
                          checked={item.isFeatured}
                          onChange={() => handleFeaturedToggle(item)}
                          disabled={loadingStates[item._id] || !(user?.role === "admin" || manager?.edit)}
                          className={`${item.isFeatured ? "bg-green-500" : "bg-gray-200"
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${loadingStates[item._id] ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        >
                          <span
                            className={`${item.isFeatured ? "translate-x-6" : "translate-x-1"
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                          />
                        </Switch>
                      </div>
                    </td>
                    <td className="text-gray-900 py-4 px-6">
                      <div className="flex justify-center space-x-3">
                        {(user?.role === "admin" || manager?.view) && (
                          <button
                            onClick={() => handleView(item)}
                            title={t("O_VIEW")}
                            className="text-slate-600 hover:text-blue-700 transition duration-150"
                          >
                            <AiFillEye className="w-5 h-5" />
                          </button>
                        )}
                        {(user?.role === "admin" || manager?.edit) && (
                          <button
                            onClick={() => handleEdit(item)}
                            title={t("O_EDIT")}
                            className="text-slate-600 hover:text-indigo-700 transition duration-150"
                          >
                            <AiFillEdit className="w-5 h-5" />
                          </button>
                        )}
                        {(user?.role === "admin" || manager?.delete) && (
                          <button
                            onClick={() => {
                              if (!item?._id) {
                                notification.error("Invalid video ID");
                                return;
                              }
                              setDeleteModal({
                                isOpen: true,
                                item: item
                              });
                            }}
                            title={t("O_DELETE")}
                            className="text-slate-600 hover:text-red-600 transition duration-150"
                          >
                            <AiFillDelete className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr className="bg-white border-b text-center">
                  <td className="py-4 px-6" colSpan={6}>
                    {t("O_NO_RECORD_FOUND")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default VideoList;